import { motion } from 'framer-motion';
import React from 'react';
import { useKeyPress } from '@/hooks';
import { Portal } from '../Portal';
import styles from './OverlayingPopup.module.scss';

// Props Types
interface OverlayingPopupProps {
	children: React.ReactNode;
	onClose?: () => void;
	classes?: {
		popup?: string;
		overlay?: string;
	};
	isShown?: boolean;
	escapeClose?: boolean;
}

// Animation
const popupMotion = {
	variants: {
		init: {
			opacity: 0,
			scale: 0.98,
		},
		animate: {
			opacity: 1,
			scale: 1,
			transition: { duration: 0.25, ease: 'easeOut' },
		},
		exit: {
			opacity: 0,
			scale: 0.98,
			transition: { duration: 0.2, ease: 'easeIn' },
		},
	},
	initial: 'init',
	animate: 'animate',
	exit: 'exit',
	onAnimationStart: (variant: string) => {
		// popup is open
		if (variant === 'animate') {
			document.body.style.overflow = 'hidden';

			return;
		}

		// more than 1 popup is open
		if (document?.querySelectorAll('[role="dialog"]')?.length > 1) {
			document.body.style.overflow = 'hidden';

			return;
		}

		// popup is closed
		document.body.style.overflow = '';
	},
};
const overlayMotion = {
	variants: {
		init: {
			opacity: 0,
		},
		animate: {
			opacity: 1,
		},
		exit: {
			opacity: 0,
		},
	},
};

export const OverlayingPopup: React.FC<OverlayingPopupProps> = ({
	children,
	onClose,
	classes,
	isShown,
	escapeClose,
}) => {
	const { popup, overlay } = classes ?? {};

	useKeyPress('Escape', onClose, escapeClose);

	return (
		<Portal>
			<motion.div
				role='dialog'
				className={`${styles['popup']} ${popup ?? ''}`}
				// Animation
				{...popupMotion}
			>
				<motion.button
					type='button'
					onClick={onClose}
					className={`${styles['popup__overlay']} ${overlay ?? ''}`}
					// Animation
					{...overlayMotion}
				/>
				{children}
			</motion.div>
		</Portal>
	);
};
